/**
 * QuestProgress.mjs
 * Progress computation for Pattern Quest v2.
 *
 * updateQuestProgress(quest, event, worldFns)
 *   → returns true if quest is now complete
 *
 * event shapes:
 *   tile:    { type:'tile',    coord, matchCount, majorType, isOcean, neighbourCount, neighbourTerrainTypes }
 *   overlay: { type:'overlay', coord, overlayFamily, adjacentOverlays, onBonusTerrain, isOcean }
 */

import { hexKey, getNeighbor } from '../math/hexMath.mjs';

// ─── BFS helpers ──────────────────────────────────────────────────────────────

function clusterBFS(tiles, startCoord, terrain, getMajorityType) {
  const startTile = tiles[hexKey(startCoord.q, startCoord.r)];
  if (!startTile || !startTile.placed) return 0;
  if (terrain && getMajorityType(startTile.sides) !== terrain) return 0;
  const actualTerrain = terrain || getMajorityType(startTile.sides);
  const visited = new Set([hexKey(startCoord.q, startCoord.r)]);
  const queue = [startCoord];
  let size = 0;
  while (queue.length > 0) {
    const { q, r } = queue.shift();
    size++;
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(q, r, s);
      const k = hexKey(nb.q, nb.r);
      if (visited.has(k)) continue;
      visited.add(k);
      const t = tiles[k];
      if (!t || !t.placed) continue;
      if (getMajorityType(t.sides) !== actualTerrain) continue;
      queue.push(nb);
    }
  }
  return size;
}

function overlayClusterBFS(tiles, startCoord, family, getOverlayFamily) {
  const startTile = tiles[hexKey(startCoord.q, startCoord.r)];
  if (!startTile || !startTile.placed || !startTile.overlaySpriteKey) return 0;
  if (getOverlayFamily(startTile.overlaySpriteKey) !== family) return 0;
  const visited = new Set([hexKey(startCoord.q, startCoord.r)]);
  const queue = [startCoord];
  let size = 0;
  while (queue.length > 0) {
    const { q, r } = queue.shift();
    size++;
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(q, r, s);
      const k = hexKey(nb.q, nb.r);
      if (visited.has(k)) continue;
      visited.add(k);
      const t = tiles[k];
      if (!t || !t.placed || !t.overlaySpriteKey) continue;
      if (getOverlayFamily(t.overlaySpriteKey) !== family) continue;
      queue.push(nb);
    }
  }
  return size;
}

/** Longest straight line of terrain T (or any terrain) through coord */
function straightLineLength(tiles, coord, terrain, getMajorityType) {
  const DIRS = [
    [{ q:1,r:0 }, { q:-1,r:0 }],
    [{ q:0,r:1 }, { q:0,r:-1 }],
    [{ q:-1,r:1 }, { q:1,r:-1 }],
  ];
  const tile = tiles[hexKey(coord.q, coord.r)];
  if (!tile || !tile.placed) return 0;
  const actualTerrain = terrain || getMajorityType(tile.sides);
  if (getMajorityType(tile.sides) !== actualTerrain) return 0;
  let maxLen = 0;
  for (const [d0, d1] of DIRS) {
    let len = 1;
    for (const d of [d0, d1]) {
      let { q, r } = coord;
      while (true) {
        q += d.q; r += d.r;
        const t = tiles[hexKey(q, r)];
        if (!t || !t.placed || getMajorityType(t.sides) !== actualTerrain) break;
        len++;
      }
    }
    maxLen = Math.max(maxLen, len);
  }
  return maxLen;
}

/** Total ocean-land border edge count across the whole board */
function coastlineEdgeCount(tiles, getMajorityType) {
  let count = 0;
  for (const t of Object.values(tiles)) {
    if (!t.placed || getMajorityType(t.sides) !== 'ocean') continue;
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(t.coord.q, t.coord.r, s);
      const nbT = tiles[hexKey(nb.q, nb.r)];
      if (!nbT || !nbT.placed) continue;
      if (getMajorityType(nbT.sides) !== 'ocean') count++;
    }
  }
  return count;
}

/** Check if any empty hex adjacent to coord has all 6 neighbours placed. Returns count of new rings. */
function checkRings(tiles, coord) {
  let rings = 0;
  for (let s = 0; s < 6; s++) {
    const candidate = getNeighbor(coord.q, coord.r, s);
    const candidateKey = hexKey(candidate.q, candidate.r);
    if (tiles[candidateKey]?.placed) continue; // not empty
    let allPlaced = true;
    for (let s2 = 0; s2 < 6; s2++) {
      const nb2 = getNeighbor(candidate.q, candidate.r, s2);
      if (!tiles[hexKey(nb2.q, nb2.r)]?.placed) { allPlaced = false; break; }
    }
    if (allPlaced) rings++;
  }
  return rings;
}

/** For biome_trio: compute how many distinct terrains have a cluster >= clusterSize */
function countBiotrioTerrains(tiles, clusterSize, getMajorityType) {
  const visited = new Set();
  const largestByTerrain = new Map();
  for (const t of Object.values(tiles)) {
    if (!t.placed) continue;
    const k = hexKey(t.coord.q, t.coord.r);
    if (visited.has(k)) continue;
    const terrain = getMajorityType(t.sides);
    // BFS
    const localVisited = new Set([k]);
    const queue = [t.coord];
    let size = 0;
    while (queue.length > 0) {
      const { q, r } = queue.shift();
      visited.add(hexKey(q, r));
      size++;
      for (let s = 0; s < 6; s++) {
        const nb = getNeighbor(q, r, s);
        const nk = hexKey(nb.q, nb.r);
        if (localVisited.has(nk)) continue;
        localVisited.add(nk);
        const nt = tiles[nk];
        if (!nt || !nt.placed || getMajorityType(nt.sides) !== terrain) continue;
        queue.push(nb);
      }
    }
    largestByTerrain.set(terrain, Math.max(largestByTerrain.get(terrain) || 0, size));
  }
  let count = 0;
  for (const sz of largestByTerrain.values()) {
    if (sz >= clusterSize) count++;
  }
  return count;
}

// ─── Helper: is progress complete? ────────────────────────────────────────────

export function isComplete(progress) {
  switch (progress.kind) {
    case 'count':    return progress.current >= progress.target;
    case 'bestSoFar':return progress.bestSoFar >= progress.target;
    case 'boolean':  return progress.done;
    case 'distinct': return progress.values.length >= progress.target;
    case 'biome_trio': return progress.achievedTerrains.length >= progress.target;
    default: return false;
  }
}

/** Human-readable progress string for the UI */
export function progressText(progress) {
  switch (progress.kind) {
    case 'count':    return `${progress.current} / ${progress.target}`;
    case 'bestSoFar':return `Best: ${progress.bestSoFar} / ${progress.target}`;
    case 'boolean':  return progress.done ? 'Complete!' : 'Not yet';
    case 'distinct': return `${progress.values.length} / ${progress.target} terrains`;
    case 'biome_trio': return `${progress.achievedTerrains.length} / ${progress.target} biomes`;
    default: return '';
  }
}

// ─── Main update entry point ──────────────────────────────────────────────────

/**
 * Update quest progress based on a placement event.
 * @param {object} quest - the active quest object (mutated in place)
 * @param {object} event - tile or overlay placement event
 * @param {object} worldFns - { tiles, getMajorityType, getOverlayFamily }
 * @returns {boolean} true if quest is now complete
 */
export function updateQuestProgress(quest, event, worldFns) {
  const { tiles, getMajorityType, getOverlayFamily } = worldFns;
  const { progress, category, params } = quest;

  // Already complete — skip
  if (isComplete(progress)) return true;

  if (event.type === 'tile') {
    _updateTile(progress, category, params, event, tiles, getMajorityType);
  } else if (event.type === 'overlay') {
    _updateOverlay(progress, category, params, event, tiles, getMajorityType, getOverlayFamily);
  }

  return isComplete(progress);
}

// ─── Tile placement updates ───────────────────────────────────────────────────

function _updateTile(progress, category, params, ev, tiles, getMT) {
  switch (category) {
    case 'expand_world': {
      if (params.minNeighbours) {
        if (ev.neighbourCount >= params.minNeighbours) progress.current++;
      } else {
        progress.current++;
      }
      break;
    }

    case 'place_tiles': {
      if (params.terrain) {
        if (ev.majorType === params.terrain) {
          if (progress.kind === 'count') progress.current++;
        }
      } else if (params.terrainSet) {
        if (params.terrainSet.includes(ev.majorType)) {
          if (progress.kind === 'count') progress.current++;
        }
      } else if (params.kinds) {
        // multi_terrain distinct progress
        if (progress.kind === 'distinct' && !progress.values.includes(ev.majorType)) {
          progress.values.push(ev.majorType);
        }
      } else if (params.minTerrainVariety) {
        // gradient: count placements where neighbour variety >= minTerrainVariety
        if (ev.neighbourTerrainTypes && ev.neighbourTerrainTypes.size >= params.minTerrainVariety) {
          progress.current++;
        }
      } else {
        if (progress.kind === 'count') progress.current++;
      }
      break;
    }

    case 'match_edges': {
      if (progress.kind === 'count') {
        if (params.minMatches) {
          if (ev.matchCount >= params.minMatches) progress.current++;
        } else {
          progress.current += ev.matchCount;
        }
      } else if (progress.kind === 'boolean') {
        if (params.minMatches && ev.matchCount >= params.minMatches) progress.done = true;
      }
      break;
    }

    case 'make_cluster': {
      if (progress.kind === 'biome_trio') {
        // Recompute how many distinct terrains have a cluster >= clusterSize
        const count = countBiotrioTerrains(tiles, progress.clusterSize, getMT);
        // Track achieved terrains as a count (just update the array length)
        progress.achievedTerrains = Array.from({ length: count }, (_, i) => i);
      } else if (progress.kind === 'bestSoFar') {
        const terrain = params.terrain || null;
        const size = clusterBFS(tiles, ev.coord, terrain, getMT);
        if (size > progress.bestSoFar) progress.bestSoFar = size;
      }
      break;
    }

    case 'make_chain': {
      if (progress.kind === 'bestSoFar') {
        const len = straightLineLength(tiles, ev.coord, params.terrain || null, getMT);
        if (len > progress.bestSoFar) progress.bestSoFar = len;
      }
      break;
    }

    case 'make_coastline': {
      if (progress.kind === 'bestSoFar') {
        const edges = coastlineEdgeCount(tiles, getMT);
        if (edges > progress.bestSoFar) progress.bestSoFar = edges;
      }
      break;
    }

    case 'make_ring': {
      const rings = checkRings(tiles, ev.coord);
      if (rings > 0) {
        if (progress.kind === 'boolean') progress.done = true;
        else if (progress.kind === 'count') progress.current = Math.min(progress.current + rings, progress.target);
      }
      break;
    }

    default: break;
  }
}

// ─── Overlay placement updates ────────────────────────────────────────────────

function _updateOverlay(progress, category, params, ev, tiles, getMT, getOF) {
  switch (category) {
    case 'place_overlays': {
      if (params.families) {
        if (params.families.includes(ev.overlayFamily)) progress.current++;
      } else if (params.bonusTerrain) {
        if (ev.onBonusTerrain) progress.current++;
      } else {
        progress.current++;
      }
      break;
    }

    case 'overlay_adjacency': {
      const adj = ev.adjacentOverlays;
      if (progress.kind === 'count') {
        if (adj >= params.minNeighbourOverlays) progress.current++;
      } else if (progress.kind === 'boolean') {
        if (adj >= params.minNeighbourOverlays) progress.done = true;
      }
      break;
    }

    case 'overlay_cluster': {
      if (progress.kind === 'bestSoFar') {
        const size = overlayClusterBFS(tiles, ev.coord, params.family, getOF);
        if (size > progress.bestSoFar) progress.bestSoFar = size;
      }
      break;
    }

    default: break;
  }
}
